/*
  formatos.ts — apresentação de data, hora, valor e período.

  Fuso único `America/Sao_Paulo` e precisão de minuto, como manda
  `docs/product/REQUIREMENTS.md`. Períodos são semiabertos `[início, fim)` e a
  interface escreve `início → fim` para lembrar disso.
*/
export const FUSO = "America/Sao_Paulo";

const data = new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, day: "2-digit", month: "2-digit", year: "numeric" });
const dataCurta = new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, day: "2-digit", month: "2-digit" });
const dataExtensa = new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, day: "numeric", month: "long", year: "numeric" });
const hora = new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, hour: "2-digit", minute: "2-digit" });
const dataHora = new Intl.DateTimeFormat("pt-BR", {
  timeZone: FUSO,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const moeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const inteiro = new Intl.NumberFormat("pt-BR");

const paraData = (valor: string | Date | null | undefined): Date | null => {
  if (!valor) return null;
  const instante = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(instante.getTime()) ? null : instante;
};

export const formatarData = (valor: string | Date | null | undefined) => {
  const instante = paraData(valor);
  return instante ? data.format(instante) : "—";
};

export const formatarDataCurta = (valor: string | Date | null | undefined) => {
  const instante = paraData(valor);
  return instante ? dataCurta.format(instante) : "—";
};

export const formatarDataExtensa = (valor: string | Date | null | undefined) => {
  const instante = paraData(valor);
  return instante ? dataExtensa.format(instante) : "—";
};

export const formatarHora = (valor: string | Date | null | undefined) => {
  const instante = paraData(valor);
  return instante ? hora.format(instante) : "—";
};

export const formatarDataHora = (valor: string | Date | null | undefined) => {
  const instante = paraData(valor);
  return instante ? dataHora.format(instante).replace(", ", " ") : "—";
};

/**
 * Período semiaberto. Quando começa e termina no mesmo dia, a data aparece uma
 * vez só: `22/09/2026 09:00 → 18:00`.
 */
export function formatarPeriodo(inicio: string | Date | null | undefined, fim: string | Date | null | undefined) {
  const de = paraData(inicio);
  const ate = paraData(fim);
  if (!de || !ate) return "—";
  const mesmoDia = data.format(de) === data.format(ate);
  return mesmoDia
    ? `${data.format(de)} ${hora.format(de)} → ${hora.format(ate)}`
    : `${dataHora.format(de).replace(", ", " ")} → ${dataHora.format(ate).replace(", ", " ")}`;
}

/** A mesma coisa, sem o ano, para tabelas apertadas. */
export function formatarPeriodoCurto(inicio: string | Date | null | undefined, fim: string | Date | null | undefined) {
  const de = paraData(inicio);
  const ate = paraData(fim);
  if (!de || !ate) return "—";
  const mesmoDia = data.format(de) === data.format(ate);
  return mesmoDia
    ? `${dataCurta.format(de)} ${hora.format(de)} → ${hora.format(ate)}`
    : `${dataCurta.format(de)} ${hora.format(de)} → ${dataCurta.format(ate)} ${hora.format(ate)}`;
}

const PADRAO_LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

export function validarPeriodo(inicio: string, fim: string): string | null {
  const de = PADRAO_LOCAL.exec(inicio);
  const ate = PADRAO_LOCAL.exec(fim);
  if (!de || !ate) return null;
  const dias = (Date.UTC(Number(ate[1]), Number(ate[2]) - 1, Number(ate[3]))
    - Date.UTC(Number(de[1]), Number(de[2]) - 1, Number(de[3]))) / 86_400_000;
  if (dias > 365) return "O período pode abranger no máximo 366 dias.";
  if (fim <= inicio) return "O fim deve ser posterior ao início.";
  return null;
}

/*
  Par de `datetime-local` cru → `dd/mm hh:mm → hh:mm`, sem passar por `Date`
  nem fuso: o valor já é o horário digitado (não tem offset para converter), e
  em várias datas, exibe os dois limites. A repetição diária só descreve linhas já agrupadas.
  Usado nos resumos que ficam no formulário
  enquanto o valor ainda mora num `EspacoDaFase`/`LinhaRecurso` em edição.
*/
export function formatarPeriodoLocal(inicio: string, fim: string, repetirDiariamente = false) {
  const partesInicio = PADRAO_LOCAL.exec(inicio);
  const partesFim = PADRAO_LOCAL.exec(fim);
  if (!partesInicio || !partesFim) return "período pendente";
  const [, anoInicio, mesInicio, diaInicio, horaInicio, minutoInicio] = partesInicio;
  const [, anoFim, mesFim, diaFim, horaFim, minutoFim] = partesFim;
  const dataInicio = `${diaInicio}/${mesInicio}${anoInicio !== anoFim ? `/${anoInicio}` : ""}`;
  const dataFim = `${diaFim}/${mesFim}${anoInicio !== anoFim ? `/${anoFim}` : ""}`;
  return inicio.slice(0, 10) === fim.slice(0, 10)
    ? `${dataInicio} ${horaInicio}:${minutoInicio} → ${horaFim}:${minutoFim}`
    : repetirDiariamente
      ? `${dataInicio} a ${dataFim} · diariamente das ${horaInicio}:${minutoInicio} às ${horaFim}:${minutoFim}`
      : `${dataInicio} ${horaInicio}:${minutoInicio} → ${dataFim} ${horaFim}:${minutoFim}`;
}

export const formatarValor = (valor: number | null | undefined) =>
  typeof valor === "number" ? moeda.format(valor) : "—";

export const formatarInteiro = (valor: number | null | undefined) =>
  typeof valor === "number" ? inteiro.format(valor) : "—";

export function formatarCep(valor: string | null | undefined) {
  const digitos = (valor ?? "").replace(/\D/g, "").slice(0, 8);
  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos;
}

export function formatarDuracao(minutos: number | null | undefined) {
  if (typeof minutos !== "number") return "—";
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto} min`;
  return resto === 0 ? `${horas} h` : `${horas} h ${String(resto).padStart(2, "0")} min`;
}

/**
 * Taxa em porcentagem. Denominador zero é “não aplicável”, nunca zero por
 * cento: a decisão de desenho 7 de `docs/design/TELAS.md` expõe o denominador
 * justamente para a taxa poder ser conferida.
 */
export function formatarTaxa(taxa: number | null | undefined, denominador?: number | null) {
  if (denominador === 0) return "não aplicável";
  if (taxa == null) return "não aplicável";
  return `${(taxa * 100).toFixed(1).replace(".", ",")}%`;
}

/* -------------------------------------------------------- entrada de datas */

/**
 * `datetime-local` → ISO 8601 com offset e precisão de minuto, que é o que a
 * API aceita. O valor digitado é lido como horário de São Paulo.
 */
export function paraIso(valorLocal: string): string | null {
  if (!valorLocal) return null;
  const partes = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(valorLocal);
  if (!partes) return null;
  const [, ano, mes, dia, horas, minutos] = partes;
  const provisorio = new Date(`${ano}-${mes}-${dia}T${horas}:${minutos}:00Z`);
  const deslocamento = deslocamentoDoFuso(provisorio);
  return `${ano}-${mes}-${dia}T${horas}:${minutos}:00${deslocamento}`;
}

/* Construir um Intl é caro; estes são fixos, então nascem uma vez com o módulo. */
const CAMPO_LOCAL = new Intl.DateTimeFormat("sv-SE", {
  timeZone: FUSO,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

/** ISO → valor de `datetime-local` no fuso de São Paulo. */
export function paraCampoLocal(valor: string | Date | null | undefined): string {
  const instante = paraData(valor);
  if (!instante) return "";
  return CAMPO_LOCAL.format(instante).replace(" ", "T");
}

/**
 * "Até" antes de "De", nos valores `aaaa-mm-dd` dos campos de data. A
 * comparação de texto vale porque o formato é fixo; campo vazio não conta.
 */
export function periodoInvertido(de: string, ate: string): boolean {
  return Boolean(de && ate) && ate < de;
}

/** Mensagem do campo "Até" quando o período está invertido. */
export const ERRO_PERIODO_INVERTIDO = "Use uma data igual ou posterior a “De”.";
/** O mesmo motivo para o corpo da lista, quando o filtro está fechado. */
export const DESCRICAO_PERIODO_INVERTIDO = "“Até” está antes de “De”. Corrija as datas no filtro.";

/** ISO → valor de `date` (só o dia) no fuso de São Paulo. */
export function paraCampoData(valor: string | Date | null | undefined): string {
  return paraCampoLocal(valor).slice(0, 10);
}

const DESLOCAMENTO = new Intl.DateTimeFormat("en-US", { timeZone: FUSO, timeZoneName: "longOffset" });

/** Offset de `America/Sao_Paulo` no instante dado, como `-03:00`. */
function deslocamentoDoFuso(instante: Date): string {
  const nome = DESLOCAMENTO.formatToParts(instante).find((parte) => parte.type === "timeZoneName")?.value ?? "GMT-03:00";
  const encontrado = /GMT([+-]\d{2}:\d{2})/.exec(nome);
  return encontrado ? encontrado[1] : "-03:00";
}

/**
 * Soma minutos a um valor de `datetime-local`, no relógio de parede.
 *
 * O valor não carrega offset — é a hora que a pessoa digitou —, então a conta
 * roda ancorada em UTC: quatro horas a mais são quatro horas no relógio, sem o
 * fuso do navegador puxar o resultado para outro dia. Valor incompleto ou fora
 * do formato devolve string vazia, que é como os campos nascem.
 */
export function deslocarCampoLocal(valorLocal: string, minutos: number): string {
  const partes = PADRAO_LOCAL.exec(valorLocal);
  if (!partes) return "";
  const [, ano, mes, dia, horas, minutosDoDia] = partes;
  const alvo = Date.UTC(Number(ano), Number(mes) - 1, Number(dia), Number(horas), Number(minutosDoDia));
  return new Date(alvo + minutos * 60_000).toISOString().slice(0, 16);
}

/**
 * Deslocamento em dias a partir de um instante, no formato de `datetime-local`.
 * O instante entra como argumento — quem renderiza usa `useAgora`.
 */
export function emDias(dias: number, horaDoDia: string, instante: number): string {
  const alvo = new Date(instante + dias * 24 * 60 * 60_000);
  return `${paraCampoData(alvo)}T${horaDoDia}`;
}

/**
 * O dia civil de São Paulo que contém o instante, da meia-noite à meia-noite
 * seguinte. Como a listagem filtra por sobreposição, a janela traz também o
 * agendamento de vários dias que está acontecendo nele.
 */
export function janelaDoDia(instante: number): { inicio: string; fim: string } {
  return {
    inicio: paraIso(emDias(0, "00:00", instante))!,
    fim: paraIso(emDias(1, "00:00", instante))!,
  };
}

/* ------------------------------------------------------- dias do calendário */

/*
  Um `AAAA-MM-DD` já é uma data civil de São Paulo: não tem instante e não
  precisa de fuso. Ancorar em UTC é o que impede o fuso do navegador de mover o
  dia para trás ou para frente na hora de contar a semana.
*/
const comoDiaCivil = (dia: string) => new Date(`${dia}T00:00:00Z`);
const DIA_DA_SEMANA_CURTO = new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC", weekday: "short" });

/** Domingo é 0, como em `Date.getUTCDay`. */
export const diaDaSemana = (dia: string) => comoDiaCivil(dia).getUTCDay();

/** `seg`, `ter`, `qua`… sem o ponto que o `pt-BR` acrescenta. */
export const formatarDiaDaSemana = (dia: string) =>
  DIA_DA_SEMANA_CURTO.format(comoDiaCivil(dia)).replace(".", "");

/** `03/09`, direto do próprio `AAAA-MM-DD`. */
export const formatarDiaEMes = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/**
 * Anda dias sobre um `AAAA-MM-DD`, ancorando ao meio-dia de São Paulo: a
 * âncora sobra folga suficiente para nenhum deslocamento de fuso trocar o dia.
 */
export const somarDias = (dia: string, dias: number) =>
  paraCampoData(new Date(new Date(paraIso(`${dia}T12:00`) ?? `${dia}T12:00:00`).getTime() + dias * 86_400_000));

/**
 * Um `AAAA-MM-DD` por extenso. O meio-dia de São Paulo é a âncora: lido como
 * hora local, o fuso do navegador podia trocar o dia mostrado.
 */
export const formatarDiaPorExtenso = (dia: string) => formatarDataExtensa(paraIso(`${dia}T12:00`) ?? dia);

/** Primeiro dia do mês de um `AAAA-MM-DD`, andando `meses` meses. */
export function inicioDoMes(dia: string, meses = 0): string {
  const alvo = new Date(Date.UTC(Number(dia.slice(0, 4)), Number(dia.slice(5, 7)) - 1 + meses, 1));
  return alvo.toISOString().slice(0, 10);
}

/** Último dia do mês de um `AAAA-MM-DD`. */
export function fimDoMes(dia: string): string {
  return somarDias(inicioDoMes(dia, 1), -1);
}

/**
 * Protocolo.
 *
 * A API devolve `ano-número` já formatado (`agendamentos.protocolo`), com o
 * número reiniciando a cada ano civil. A interface só acrescenta o prefixo do
 * tipo, que ela conhece e o banco não formata.
 */
export function formatarProtocolo(protocolo: string | null | undefined, tipo?: string | null): string {
  if (!protocolo) return "—";
  const prefixo = tipo === "reuniao" ? "REU" : tipo === "bloqueio_administrativo" ? "BLQ" : tipo === "evento" ? "EVT" : null;
  return prefixo ? `${prefixo}-${protocolo}` : `#${protocolo}`;
}
